/**
 * Event heads — the one member a committee puts in charge of an event.
 *
 * A head is NOT a role on the roster (`member|coach|admin` is unchanged). It is
 * a job attached to one event: while that event is open, the head may log race
 * results and volunteer hours for everybody who took part, which is otherwise
 * committee-only work. The committee's "Mark completed" closes the job.
 *
 * Every predicate here is twinned in `apps/api/src/shared/lead.ts`, because the
 * API has to answer the same questions before it accepts a write. The rail
 * hiding a tab is convenience; the server refusing the POST is the rule.
 */

/** Is this member the head of this event? */
export function isEventHead(event, memberId) {
  return !!event && !!memberId && event.headId === memberId;
}

/**
 * May this member log contributions against this event right now?
 *
 * Head, and the event still open. A completed event is a closed book: its
 * distance is already on the club board and its results are the club's record,
 * so late edits go through the committee.
 */
export function canLogForEvent(event, memberId) {
  return isEventHead(event, memberId) && !event.completedAt;
}

/**
 * The events this member is currently leading — one extra tab each in their
 * left panel. Sorted by date so the nearest job sits highest.
 */
export function openLeadEvents(events, memberId) {
  return events
    .filter((e) => canLogForEvent(e, memberId))
    .sort((a, b) => a.date.localeCompare(b.date));
}

/**
 * How many events this member has led to completion.
 *
 * Deliberately counts COMPLETED events, not assigned ones: "events led" should
 * mean a finished job. It also means the tally rises at the same moment the
 * tab disappears, so the two never disagree about whether the work is done.
 */
export function eventsLedCount(events, memberId) {
  return events.filter((e) => isEventHead(e, memberId) && !!e.completedAt).length;
}
